import React from "react";
import { ListChildComponentProps } from "react-window";
import { HighlightedText } from "../searchUtils";
import { GridSelection } from "./gridGeometry";
import { SheetColumn, SheetRow } from "./sheetTypes";

/**
 * One row as a card: the first visible column is the heading, the rest are
 * "label: value" lines. It exists for phone width, where a 34-column table is
 * only readable by dragging it sideways.
 *
 * Values stay on one line with an ellipsis. That is what keeps the card height
 * predictable enough for a fixed-size virtual list; the full text is one tap
 * away through the cell detail popover.
 */

export const CARD_PADDING = 10;
export const CARD_TITLE_HEIGHT = 24;
export const CARD_FIELD_HEIGHT = 20;

export function cardHeight(columnCount: number): number {
  const fields = Math.max(0, columnCount - 1);
  return CARD_PADDING * 2 + CARD_TITLE_HEIGHT + fields * CARD_FIELD_HEIGHT;
}

export interface CardItemData {
  rows: SheetRow[];
  columns: SheetColumn[];
  query: string;
  activeMatch: { rowPos: number; colPos: number } | null;
  selection: GridSelection | null;
  onCardDown: (rowPos: number, extend: boolean) => void;
  onValueExpand: (rowPos: number, colPos: number, anchor: HTMLElement) => void;
}

function isRowSelected(
  selection: GridSelection | null,
  rowPos: number,
): boolean {
  if (!selection) return false;
  const top = Math.min(selection.anchorRow, selection.focusRow);
  const bottom = Math.max(selection.anchorRow, selection.focusRow);
  return rowPos >= top && rowPos <= bottom;
}

const SheetCardRow: React.FC<ListChildComponentProps<CardItemData>> = ({
  index,
  style,
  data,
}) => {
  const {
    rows,
    columns,
    query,
    activeMatch,
    selection,
    onCardDown,
    onValueExpand,
  } = data;

  const row = rows[index];
  if (!row) return null;

  const selected = isRowSelected(selection, index);
  const isActiveRow = activeMatch != null && activeMatch.rowPos === index;
  const titleColumn = columns[0];
  const titleValue = titleColumn
    ? (row.cells[titleColumn.sourceIndex] ?? "")
    : "";

  return (
    <div
      style={{
        ...style,
        boxSizing: "border-box",
        padding: CARD_PADDING,
        borderBottom: "1px solid #eee",
        backgroundColor: selected
          ? "rgba(25,118,210,0.12)"
          : isActiveRow
            ? "rgba(255,152,0,0.10)"
            : "#fff",
        cursor: "pointer",
      }}
      onMouseDown={(event) => {
        if (event.button !== 0) return;
        onCardDown(index, event.shiftKey);
      }}
    >
      <div
        style={{
          height: CARD_TITLE_HEIGHT,
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={titleValue}
          onMouseDown={(event) => {
            if (event.detail < 2) return;
            event.stopPropagation();
            onValueExpand(index, 0, event.currentTarget);
          }}
        >
          {query ? (
            <HighlightedText text={titleValue} query={query} />
          ) : (
            titleValue
          )}
        </span>
        <span style={{ fontSize: 11, color: "#666", flexShrink: 0 }}>
          Row {row.sourceIndex + 1}
        </span>
      </div>

      {columns.slice(1).map((column, offset) => {
        const colPos = offset + 1;
        const raw = row.cells[column.sourceIndex];
        const value = raw == null ? "" : raw;
        const isActiveCell = isActiveRow && activeMatch!.colPos === colPos;
        return (
          <div
            key={column.id}
            style={{
              height: CARD_FIELD_HEIGHT,
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
            }}
          >
            <span
              style={{
                // A share of the width on a phone, but capped so a wide dialog
                // does not strand the value half a screen from its label.
                width: "40%",
                maxWidth: 220,
                flexShrink: 0,
                color: "#666",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={column.label || column.letter}
            >
              {column.label || column.letter}
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                backgroundColor: isActiveCell
                  ? "rgba(255,152,0,0.25)"
                  : undefined,
              }}
              title={value}
              onMouseDown={(event) => {
                if (event.detail < 2) return;
                event.stopPropagation();
                onValueExpand(index, colPos, event.currentTarget);
              }}
            >
              {query ? <HighlightedText text={value} query={query} /> : value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(SheetCardRow);
